// ============================================================================
// useSchemaOverrides Hook
// ============================================================================
// Provides schema-aware field visibility and validation helpers for CRUD forms.
//
// Usage:
//   const { isVisible, isRequired, fieldConfig } = useSchemaOverrides("devices", "postDevice");
//   if (isVisible("serial")) { /* render field */ }
//   <input required={isRequired("serial")} />
// ============================================================================

import { useMemo } from "react";
import {
  getEndpointFields,
  isFieldVisible,
  isFieldRequired,
  getFieldConfig,
} from "../config/schemaConfig";
import type { FieldConfig } from "../config/schemas/types";

interface SchemaOverridesResult {
  /** Check if a field should be rendered in the form */
  isVisible: (field: string) => boolean;
  /** Check if a field should have required validation */
  isRequired: (field: string) => boolean;
  /** Get full field config (visible + required) */
  fieldConfig: (field: string) => FieldConfig;
  /** All resolved field configs for this endpoint */
  allFields: Record<string, FieldConfig>;
  /** List of visible field names */
  visibleFields: string[];
}

/**
 * Hook that returns schema override helpers scoped to a specific entity + endpoint.
 *
 * @param entity  - The entity name (e.g. "devices", "vehicles", "drivers")
 * @param endpoint - The endpoint name (e.g. "postDevice", "putVehicle")
 */
export function useSchemaOverrides(
  entity: string,
  endpoint: string,
): SchemaOverridesResult {
  const allFields = useMemo(
    () => getEndpointFields(entity, endpoint),
    [entity, endpoint],
  );

  const visibleFields = useMemo(
    () =>
      Object.entries(allFields)
        .filter(([, cfg]) => cfg.visible)
        .map(([name]) => name),
    [allFields],
  );

  const isVisible = useMemo(
    () => (field: string) => isFieldVisible(entity, endpoint, field),
    [entity, endpoint],
  );

  const isRequired = useMemo(
    () => (field: string) => isFieldRequired(entity, endpoint, field),
    [entity, endpoint],
  );

  const fieldConfigFn = useMemo(
    () => (field: string) => getFieldConfig(entity, endpoint, field),
    [entity, endpoint],
  );

  return {
    isVisible,
    isRequired,
    fieldConfig: fieldConfigFn,
    allFields,
    visibleFields,
  };
}

export default useSchemaOverrides;
